import { useMemo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableContainer from "../../../../components/Tables/TableContainer";
import TableCell from "../../../BillingAndPayments/components/TableCell";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  OWNER_AD_SPEND_COLUMN_ADVERTISER,
  OWNER_AD_SPEND_COLUMN_PLATFORMS,
  OWNER_AD_SPEND_COLUMN_SHARE,
  OWNER_AD_SPEND_COLUMN_SPEND,
  OWNER_AD_SPEND_ESTIMATED_BADGE,
  OWNER_AD_SPEND_TOP_ADVERTISERS_ARIA,
  OWNER_AD_SPEND_TOP_ADVERTISERS_TITLE,
} from "../../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../../utils/constants";
import AdSpendPlatformIcons from "./AdSpendPlatformIcons";
import {
  AD_SPEND_CARD_SX,
  formatAdSpendCountry,
  formatAdSpendPercent,
  formatAdSpendUsd,
} from "./adSpendDisplay";

const headerCellProps = { showTooltip: false, isHeader: true };
const bodyCellProps = {
  showTooltip: false,
  isHeader: false,
  showLastRowBorder: true,
};

const AdSpendTopAdvertisersTable = ({ rows = [], isLoading = false }) => {
  const { t, i18n } = useTranslationApp();
  const language = i18n.language;
  const tableKeys = useMemo(() => rows.map((row) => row.advertiserID), [rows]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t(OWNER_AD_SPEND_TOP_ADVERTISERS_TITLE)}
      </Typography>
      <Box sx={{ ...AD_SPEND_CARD_SX, borderRadius: BORDER_RADIUS }}>
        <TableContainer
          loading={isLoading}
          keys={tableKeys}
          hasPagination={false}
          ariaLabel={t(OWNER_AD_SPEND_TOP_ADVERTISERS_ARIA)}
          minTableWidth={760}
        >
          <TableHead>
            <TableRow>
              <TableCell {...headerCellProps} sx={{ pl: "16px !important" }}>
                #
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_ADVERTISER)}
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_SPEND)}
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_SHARE)}
              </TableCell>
              <TableCell {...headerCellProps}>
                {t(OWNER_AD_SPEND_COLUMN_PLATFORMS)}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isLastRow = index === rows.length - 1;
              const showEstimated = row.estimatedSpendUSD > 0;
              return (
                <TableRow key={row.advertiserID}>
                  <TableCell
                    {...bodyCellProps}
                    isLastRow={isLastRow}
                    sx={{ pl: "16px !important" }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="body2">
                        {row.businessName}
                      </Typography>
                      {showEstimated ? (
                        <Chip
                          size="small"
                          label={t(OWNER_AD_SPEND_ESTIMATED_BADGE)}
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      ) : null}
                    </Box>
                    {row.businessCountry ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatAdSpendCountry({
                          countryCode: row.businessCountry,
                          t,
                        })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    {formatAdSpendUsd({ value: row.spendUSD, language })}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    {formatAdSpendPercent({
                      percent: row.sharePercent,
                      language,
                    })}
                  </TableCell>
                  <TableCell {...bodyCellProps} isLastRow={isLastRow}>
                    <AdSpendPlatformIcons platforms={row.platforms} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default AdSpendTopAdvertisersTable;
